'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { track, TrackingEventName } from '@heidi/analytics/events';
import { ScreenName } from '@heidi/analytics/vocabulary';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useQueuedModal } from '@heidi/utils';
import { healthNzMigrationPromptSuppressedAtom } from '@heidi/workspace/data';
import { useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { healthNzMigrationModalMessages } from './health-nz-migration-modal.messages';
import type {
  HealthNzDismissal,
  HealthNzMigrationModalHost,
} from './health-nz-migration-modal.types';

const HealthNzMigrationModalContent = dynamic(
  () =>
    import('./health-nz-migration-modal-content').then(
      (mod) => mod.HealthNzMigrationModalContent
    ),
  { ssr: false }
);

/**
 * Static notice: accounts on a Health NZ email move to the Health NZ team at
 * month end unless the user switches to a personal address. The flag is the
 * whole of the targeting.
 *
 * Title lives in the shell, not the lazy content, so the dialog has an
 * accessible name from the first frame.
 */
export function HealthNzMigrationModal({ host }: { host: HealthNzMigrationModalHost }) {
  const { isOpen, dismissible, handleClose } = useQueuedModal(
    ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL
  );
  // `onOpenChangeComplete` only fires on a *change*, so a shell mounting
  // already-open would never render its body.
  const [isDialogVisible, setIsDialogVisible] = useState(isOpen);
  const setSuppressed = useSetAtom(healthNzMigrationPromptSuppressedAtom);
  const viewTrackedRef = useRef(false);

  // At most once — the queue can re-surface this after it yields, which would
  // otherwise inflate the funnel.
  useEffect(() => {
    if (isOpen && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      track({
        event_name: TrackingEventName.ModalViewed,
        props: { screen_name: ScreenName.HealthNzMigrationModal },
        options: { amplitude: false },
      });
    }
  }, [isOpen]);

  const handleDismiss = useCallback(
    (reason: HealthNzDismissal) => {
      if (reason === 'acknowledged') {
        // Records the dismissal in `seen_modals`, so it holds across devices.
        handleClose();
        return;
      }
      setSuppressed(true);
      // Drops a user-triggered entry (force-show / dev tools); the launch entry
      // leaves via the suppression atom above.
      host.dequeueModal(ModalId.CUSTOM_AUDIENCE_HEALTH_NZ_MIGRATION_MODAL);
    },
    [handleClose, host, setSuppressed]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      // Only Esc, backdrop and the close X reach this path, so `modal_dismissed`
      // counts abandonment — never a CTA.
      if (open || !dismissible) return;
      track({
        event_name: TrackingEventName.ModalDismissed,
        props: {
          screen_name: ScreenName.HealthNzMigrationModal,
          auto_dismissed: false,
        },
        options: { amplitude: false },
      });
      handleDismiss('deferred');
    },
    [dismissible, handleDismiss]
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={setIsDialogVisible}
    >
      <DialogContent
        className="max-w-[470px]"
        dismissible={dismissible}
        // No DialogDescription — the body is the description.
        aria-describedby={undefined}
        data-testid="health-nz-migration-modal"
      >
        {/* Clear the absolutely-positioned close icon. */}
        <DialogHeader className="pe-6">
          <DialogTitle>
            <FormattedMessage {...healthNzMigrationModalMessages.heading} />
          </DialogTitle>
        </DialogHeader>
        {/* Not gated on `isOpen`: the close animation outlives it. */}
        {isDialogVisible && (
          <HealthNzMigrationModalContent host={host} onDismiss={handleDismiss} />
        )}
      </DialogContent>
    </Dialog>
  );
}
